import { useMemo } from "react";
import { useSdk } from "../../contexts/services/hooks/use-services-context";
import { USER_SIGNATURES_KEY } from "../../managers/user/queries";
import type { UserSignature } from "../../managers/user/types/user";
import { useSessionQuery, type LiveState } from "../session/useSessionQuery";

export function useUserSignatureList(): LiveState<UserSignature[]> {
    return useSessionQuery(USER_SIGNATURES_KEY, (sdk) =>
        sdk.userManager.getUserSignatures(),
    );
}

export type UserSignatures = {
    getUserSignatures: () => Promise<UserSignature[]>;
    createUserSignature: (
        userSignature: Omit<UserSignature, "id" | "userId" | "isFavorite">,
    ) => Promise<UserSignature>;
    updateUserSignature: (
        userSignature: UserSignature,
    ) => Promise<UserSignature>;
    deleteUserSignature: (signatureId: string) => Promise<boolean>;
};

/** Les écritures réussies sont reportées dans `useUserSignatureList`. */
export function useUserSignatures(): UserSignatures {
    const { userManager, queryClient } = useSdk();

    return useMemo<UserSignatures>(() => {
        const updateList = (
            update: (signatures: UserSignature[]) => UserSignature[],
        ) =>
            queryClient.setQueryData<UserSignature[]>(
                USER_SIGNATURES_KEY,
                (signatures) => update(signatures ?? []),
            );

        return {
            getUserSignatures: () => userManager.getUserSignatures(),
            createUserSignature: async (userSignature) => {
                const created =
                    await userManager.createUserSignature(userSignature);
                updateList((signatures) => [...signatures, created]);
                return created;
            },
            updateUserSignature: async (userSignature) => {
                const updated =
                    await userManager.updateUserSignature(userSignature);
                updateList((signatures) =>
                    signatures.map((signature) =>
                        signature.id === updated.id ? updated : signature,
                    ),
                );
                return updated;
            },
            deleteUserSignature: async (signatureId) => {
                const deleted =
                    await userManager.deleteUserSignature(signatureId);
                if (deleted) {
                    updateList((signatures) =>
                        signatures.filter(
                            (signature) => signature.id !== signatureId,
                        ),
                    );
                }
                return deleted;
            },
        };
    }, [userManager, queryClient]);
}
